import { avgRate, completionPrincipleOf, rateCardAllowedFor, roleRef } from '@timelog/pm-shared';
import type { RoleRef } from '@timelog/pm-shared';

import { usePlanEditor } from './editor';

import { useBudgetModel } from '@/analytics';
import { useMe } from '@/api';
import { useTimeLogRates } from '@/api/timelogHooks';

export type RoleOption = {
  roleRef: RoleRef;
  /** The amount an hour of this role prices at, or null when nothing prices it. */
  rate: number | null;
  /**
   * What an hour of this role COSTS, or null when unknown (ADR 0042): the
   * card entry's `costRate` on T&M, else the catalogue's stand-in price — which
   * also rides `roleRef.snapshot.costRate` into the plan when the role is picked.
   */
  costRate: number | null;
  source: 'rate-card' | 'default-list';
};

/**
 * How the listed roles price on this task (D15):
 * - `unpriced`  — no contract: names only, hours count, nothing is priced (D7);
 * - `card`      — a T&M contract: the card's amount per role (BR-11);
 * - `avg-rate`  — fixed price under Work Completed: one average rate, any role (CALC-23);
 * - `cost-to-cost` — fixed price under Cost-To-Cost: revenue derives from cost, no amount.
 */
export type RolePricing = 'unpriced' | 'card' | 'avg-rate' | 'cost-to-cost';

/**
 * The role list an allocation editor offers, decided by the task's contract
 * (D15) — the picker is per task, never per project (D8):
 *
 * | task's contract        | roles listed                 | amount shown          |
 * |------------------------|------------------------------|-----------------------|
 * | none                   | the default hourly-rate list | none                  |
 * | T&M                    | that contract's rate card    | the card's amount     |
 * | fixed, Work Completed  | the default hourly-rate list | avgRate on every row  |
 * | fixed, Cost-To-Cost    | the default hourly-rate list | none                  |
 *
 * The default list is the caller's legal entity's active hourly-rate services
 * (`GET /api/tlp/rates`, D1/D18 — the API scopes it and dedupes per ServiceID);
 * its own target amounts are never shown here. Roles are ServiceIDs.
 */
export const useRoleOptions = (contractId: string | undefined) => {
  const { contractInputs, state } = usePlanEditor();
  const me = useMe();
  // BR-30 / ADR 0041: the principle is a budget-model setting, read on load.
  const completionPrinciple = completionPrincipleOf(useBudgetModel().config);
  // The default list is TimeLog's rate catalog, so its availability is the TIMELOG_MODE knob.
  const lookupsEnabled = (me.data?.features.timelog ?? 'off') !== 'off';

  const contract = contractInputs.find((candidate) => candidate.id === contractId);
  const usesCard = contract !== undefined && rateCardAllowedFor(contract.type);
  const defaultList = useTimeLogRates(!usesCard);

  const pricing: RolePricing =
    contract === undefined
      ? 'unpriced'
      : usesCard
        ? 'card'
        : completionPrinciple === 'WORK_COMPLETED'
          ? 'avg-rate'
          : 'cost-to-cost';

  const contractAvgRate =
    pricing === 'avg-rate' && contract
      ? avgRate(contract.document, state.document.blendedCostRate)
      : null;

  const roleOptions: RoleOption[] = usesCard
    ? contract.document.rateCard.map((entry) => ({
        roleRef: entry.roleRef,
        rate: entry.rate,
        costRate: entry.costRate ?? null,
        source: 'rate-card',
      }))
    : (defaultList.data?.items ?? []).map((item) => ({
        roleRef: roleRef(item.serviceId, item.name, new Date().toISOString(), item.costRate),
        rate: contractAvgRate,
        costRate: item.costRate ?? null,
        source: 'default-list',
      }));

  return {
    roleOptions,
    lookupsEnabled,
    pricing,
    avgRate: contractAvgRate,
    /*
      Why the list may be empty, for the picker to explain (`roleCatalogNotice`):
      the default list comes from TimeLog through the proxy and can time out.
    */
    catalogFailed: !usesCard && defaultList.isError,
    hasContract: contract !== undefined,
    /** Which list the roles come from, and whose entity the default list is (D18). */
    source: usesCard ? ('rate-card' as const) : ('default-list' as const),
    legalEntityName: usesCard ? undefined : defaultList.data?.legalEntity.name,
  };
};
